import React, { useEffect, useState } from "react";
import api, { fileUrl, uploadImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty, Badge, Modal, Button, Field } from "@/components/ui-kit";
import { User, Pencil, Camera, HeartHandshake } from "lucide-react";
import { toast } from "sonner";

export default function CommitteePublic() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [bio, setBio] = useState("");
  const [photo, setPhoto] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/committee/public").then((r) => setPeople(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openEdit = (c) => { setEditing(c); setBio(c.bio || ""); setPhoto(c.photo || ""); };
  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { setPhoto(await uploadImage(file)); }
    catch { toast.error("Envoi de la photo échoué"); }
    finally { setUploading(false); }
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/coaches/${editing.source}/${editing.id}/profile`, { photo, bio });
      toast.success("Profil mis à jour");
      setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="committee-public-page">
      <PageHeader title="Le Comité des parents" subtitle="Les parents bénévoles qui font vivre le club au quotidien." />
      {loading ? (
        <Empty text="Chargement..." />
      ) : people.length ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {people.map((c, i) => (
            <div key={`${c.source}-${c.id}`} data-testid="committee-person-card"
              className="group relative bg-card border border-border rounded-2xl overflow-hidden flex flex-col animate-fade-up hover:border-primary/60 transition-colors"
              style={{ animationDelay: `${i * 60}ms` }}>
              <div className="relative aspect-[4/3] bg-secondary overflow-hidden">
                {c.photo ? (
                  <img src={fileUrl(c.photo)} alt={`${c.first_name} ${c.last_name}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground"><User size={56} strokeWidth={1.2} /></div>
                )}
                <div className="absolute top-3 left-3">
                  <Badge color="green"><span className="flex items-center gap-1"><HeartHandshake size={12} /> Comité</span></Badge>
                </div>
                {isAdmin && (
                  <button data-testid={`edit-committee-person-${c.id}`} onClick={() => openEdit(c)}
                    className="absolute top-3 right-3 p-2 rounded-full bg-black/60 text-white hover:bg-primary transition-colors" title="Modifier le profil">
                    <Pencil size={15} />
                  </button>
                )}
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-display text-lg font-bold uppercase tracking-tight leading-tight">{c.first_name} {c.last_name}</h3>
                {c.role && <p className="text-xs uppercase tracking-widest text-primary font-bold mt-1">{c.role}</p>}
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed flex-1">{c.bio || "Présentation à venir."}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Empty text="Aucun membre du comité pour le moment. Ajoutez des membres via la rubrique Utilisateurs." />
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Modifier le profil">
        {editing && (
          <div>
            <p className="text-sm text-muted-foreground mb-4"><b className="text-foreground">{editing.first_name} {editing.last_name}</b></p>
            <Field label="Photo">
              <div className="flex items-center gap-4">
                {photo ? (
                  <img src={fileUrl(photo)} alt="aperçu" className="w-16 h-16 rounded-full object-cover border border-border" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center text-muted-foreground"><User size={26} /></div>
                )}
                <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-secondary border border-border text-sm font-semibold cursor-pointer hover:border-primary transition-colors">
                  <Camera size={15} /> {uploading ? "Envoi..." : (photo ? "Changer la photo" : "Ajouter une photo")}
                  <input data-testid="committee-photo-input" type="file" accept="image/*" className="hidden" onChange={onPhoto} disabled={uploading} />
                </label>
              </div>
            </Field>
            <Field label="Biographie">
              <textarea data-testid="committee-bio-input" rows={4} value={bio} onChange={(e) => setBio(e.target.value)}
                placeholder="Rôle au sein du comité, implication..."
                className="w-full rounded-lg bg-secondary border border-border p-3 text-sm outline-none focus:border-primary resize-y" />
            </Field>
            <Button data-testid="save-committee-profile-btn" onClick={save} disabled={saving || uploading} className="w-full mt-2">
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
}
